import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const VIDEO_API = `${process.env.REACT_APP_BACKEND_URL}/api/static/videos`;
const CLIPS = [
  { slug: "hero-walkthrough-2", caption: "Settlement approved. Final paperwork on the way." },
  { slug: "accounting-trustee-1", caption: "Your 1.5% fee is one line — paid only at settlement." },
];

/**
 * Auto-advancing hero walkthrough. Plays the first clip, then crossfades to
 * the next when it ends. Click-to-play/pause control; muted+playsInline so
 * mobile browsers permit autoplay.
 */
export default function HeroWalkthroughVideo() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);

  const advance = () => setIdx((i) => (i + 1) % CLIPS.length);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    if (playing) v.play().catch(() => {});
  }, [idx, playing]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const c = CLIPS[idx];
  return (
    <figure className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 group" data-testid="hero-walkthrough">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        onEnded={advance}
        onClick={toggle}
        className="w-full aspect-video bg-black cursor-pointer"
        data-testid="hero-walkthrough-video"
      >
        <source src={`${VIDEO_API}/${c.slug}`} type="video/mp4" />
      </video>

      {/* Bottom gradient + caption */}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
        <div className="flex items-center justify-between gap-3">
          <span className="text-white text-sm font-medium drop-shadow" data-testid="hero-walkthrough-caption">
            {c.caption}
          </span>
          <div className="flex items-center gap-1.5" data-testid="hero-walkthrough-dots">
            {CLIPS.map((_, i) => (
              <span
                key={i}
                className={`block h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40"}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Centered play/pause button (only visible on hover when playing). */}
      <button
        type="button"
        onClick={toggle}
        className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
        aria-label={playing ? "Pause walkthrough" : "Play walkthrough"}
        data-testid="hero-walkthrough-toggle"
      >
        <span className="bg-black/55 backdrop-blur-sm rounded-full p-4 text-white">
          {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
        </span>
      </button>

      <figcaption className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] text-white/80 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
        Member portal walkthrough
      </figcaption>
    </figure>
  );
}
